import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLiveEvents } from "@/lib/liveEvents";
import { toast } from "sonner";
import { Send, Check, MessageSquare, ArrowLeft } from "lucide-react";

export default function Threads() {
  const { user } = useAuth();
  const { on } = useLiveEvents();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);
  const activeIdRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/signals");
    setThreads(data.threads);
    setActive((prev) => {
      if (!prev) return null;
      const fresh = data.threads.find((t) => t.id === prev.id);
      return fresh || prev;
    });
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { activeIdRef.current = active?.id || null; }, [active?.id]);

  // Real-time: refresh on any live event that touches threads.
  useEffect(() => {
    const unsubs = [
      on("message_new", () => load()),
      on("signal_new", () => load()),
      on("thread_change", () => load()),
    ];
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto mark-read when opening a thread with unread messages
  useEffect(() => {
    if (active?.id && active.unread > 0) {
      api.post(`/signals/${active.id}/read`).then(load).catch(() => {});
    }
     
  }, [active?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !active) return;
    const text = draft.trim();
    setDraft("");
    try {
      await api.post(`/signals/${active.id}/message`, { text });
      await load();
    } catch { toast.error("Failed to send"); }
  };

  const accept = async () => {
    await api.post(`/signals/${active.id}/accept`);
    toast.success("Signal accepted");
    await load();
  };

  const decline = async () => {
    await api.post(`/signals/${active.id}/decline`);
    toast("Signal declined — they can't ping you again", { description: "Manage blocked users in Profile." });
    setActive(null);
    await load();
  };

  const otherName = useMemo(() => active?.other?.display_name || "Signal", [active]);

  return (
    <div className="grid md:grid-cols-[320px_1fr] min-h-[calc(100vh-58px)]">
      {/* Thread list */}
      <aside className={`border-r border-white/5 ${active ? "hidden md:block" : ""}`}>
        <div className="p-4 border-b border-white/5">
          <div className="text-xs tracking-[0.3em] font-mono text-[#00E5FF]">// THREADS</div>
          <h2 className="font-[Unbounded] text-2xl mt-1">Signals</h2>
        </div>
        {threads.length === 0 ? (
          <div className="p-6 text-white/40 text-sm">No signals yet. Send one from the radar.</div>
        ) : (
          <ul>
            {threads.map((t) => {
              const last = t.messages?.[t.messages.length - 1];
              const isMine = last?.from === user?.id;
              return (
                <li key={t.id}>
                  <button
                    data-testid={`thread-${t.id}`}
                    onClick={() => setActive(t)}
                    className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-all ${active?.id === t.id ? "bg-white/5" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{t.other?.display_name}</span>
                      <div className="flex items-center gap-1.5">
                        {t.unread > 0 && <span data-testid={`unread-dot-${t.id}`} className="w-2 h-2 rounded-full bg-[#FF007F] glow-primary" />}
                        {t.status === "pending" && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#FF007F]/20 text-[#FF007F]">PENDING</span>}
                        {t.status === "accepted" && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#39FF14]/20 text-[#39FF14]">LINKED</span>}
                      </div>
                    </div>
                    <p className="text-xs text-white/40 truncate mt-1">
                      {last ? `${isMine ? "You: " : ""}${last.text}` : "No messages yet"}
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      {/* Conversation */}
      <section className={`flex flex-col ${active ? "" : "hidden md:flex"}`}>
        {!active ? (
          <div className="flex-1 grid place-items-center text-white/40">
            <div className="text-center">
              <MessageSquare className="mx-auto mb-3 text-[#00E5FF]" size={28} />
              <p>Pick a signal to open the thread.</p>
            </div>
          </div>
        ) : (
          <>
            <header className="flex items-center gap-3 px-4 md:px-6 py-3 border-b border-white/5 glass">
              <button data-testid="thread-back-btn" onClick={() => setActive(null)} className="md:hidden text-white/60"><ArrowLeft size={18} /></button>
              <div className="flex-1">
                <div className="font-[Unbounded]">{otherName}</div>
                <div className="text-[10px] tracking-widest font-mono text-white/40 uppercase">{active.status}</div>
              </div>
              {active.status === "pending" && active.recipient_id === user?.id && (
                <div className="flex items-center gap-2">
                  <button data-testid="decline-signal-btn" onClick={decline} className="inline-flex items-center gap-1 border border-white/15 text-white/70 text-xs font-semibold px-3 py-1.5 rounded-full hover:border-[#FF007F] hover:text-[#FF007F] transition-all">
                    Decline
                  </button>
                  <button data-testid="accept-signal-btn" onClick={accept} className="inline-flex items-center gap-1 bg-[#39FF14] text-black text-xs font-semibold px-3 py-1.5 rounded-full hover:glow-secondary transition-all">
                    <Check size={12} /> Accept
                  </button>
                </div>
              )}
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-2">
              {(active.messages || []).map((m) => {
                const mine = m.from === user?.id;
                return (
                  <div key={`${m.at}-${m.from}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2 rounded-2xl ${mine ? "bg-[#00E5FF] text-black" : "bg-white/5 border border-white/10"}`}>
                      <p className="text-sm">{m.text}</p>
                    </div>
                  </div>
                );
              })}
              {(active.messages || []).length === 0 && (
                <p className="text-center text-white/40 text-sm py-6">Say hi — this signal is quiet.</p>
              )}
            </div>

            <form onSubmit={send} className="border-t border-white/5 p-3 flex gap-2">
              <input
                data-testid="thread-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={active.status !== "accepted"}
                placeholder={active.status === "accepted" ? "Send a message…" : "Waiting for the other side to accept…"}
                className="flex-1 bg-[#0D0E15] border border-white/10 rounded-full px-4 py-2.5 focus:border-[#00E5FF] focus:outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                data-testid="thread-send-btn"
                type="submit"
                disabled={active.status !== "accepted" || !draft.trim()}
                className="bg-[#00E5FF] text-black font-semibold px-4 py-2 rounded-full hover:glow-primary transition-all inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
              >
                <Send size={14} /> Send
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
